<script>
  import { _ } from 'svelte-i18n'
	import { tick } from 'svelte'
  import { modals, previews_font_size, previews_are_zoomed, dark_theme } from '../../stores'
	import { createImage, saveImage } from '../../modules/exportPreviews'
  import { tweened } from 'svelte/motion'
  import { cubicOut } from 'svelte/easing'
  import Checkbox from '../../elements/Checkbox.svelte'
  import Fieldset from '../../elements/Fieldset.svelte'
  import Button from '../../elements/Button.svelte'
  import Modal from '../../elements/Modal.svelte'
  import Label from '../../elements/Label.svelte'
  import HorizontalOptionsList from '../../elements/HorizontalOptionsList.svelte'
  import PreviewsSizeStepper from '../PreviewsSizeStepper.svelte'
  
  export let element_to_export
  export let prepare_for_export
  export let export_without_bg
  export let export_without_fontname
  export let export_with_inverted_theme

  const modal_id = 'export_png'
  let modal_is_visible
  let preview_wrapper
  let created_canvas

  $: checked_export_without_fontname = $previews_are_zoomed ? true : false
  $: checked_export_without_bg = $previews_are_zoomed ? true : false
  $: prefered_export_scale = $previews_are_zoomed ? 2 : 1
  let checked_export_inverted_theme = false
  let export_preview_zoom = false

  let coords = tweened({ x: 50, y: 50 }, {
		duration: 500,
		easing: cubicOut
  })
  
  $: {
    if (modal_is_visible) checked_export_without_bg, checked_export_without_fontname, checked_export_inverted_theme, prefered_export_scale, $previews_font_size, preparePng()
  }

  async function preparePng() {
    if (!element_to_export) return
    prepare_for_export = true
    export_without_bg = checked_export_without_bg
    export_without_fontname = checked_export_without_fontname
    export_with_inverted_theme = checked_export_inverted_theme
		await(tick)
    createImage(element_to_export, preview_wrapper, prefered_export_scale, getCreatedCanvas)
    prepare_for_export = export_without_bg = export_without_fontname = export_with_inverted_theme = false
  }

  function getCreatedCanvas(canvas) {
    created_canvas = canvas
  }

  let scale_options = [{value: 1, display:$_('modals.exportPng.normal')}, {value: 2, display:$_('modals.exportPng.high')}, {value: 4, display:$_('modals.exportPng.highest')}]

</script>

<Modal id={modal_id} bind:is_visible={modal_is_visible}>
  
  <span slot="header">
    {$_('modals.exportPng.title')}
  </span>

  <span slot="content">
    {#if !$previews_are_zoomed}
      <p>Download your selection as a reference for yourself or to share with others.</p>
      <Fieldset>
        <Label>Item Size</Label>
        <PreviewsSizeStepper/>
      </Fieldset>
    {:else}
      <p>Download as a reference for yourself or to import into other apps.</p>
      <p class="faded">You can hide the font name and background to continue working with your text in other apps.</p>
    {/if}
    <Fieldset>
      <Label>{$_('modals.exportPng.resolution')}</Label>
      <HorizontalOptionsList list_options={scale_options} bind:selected_option={prefered_export_scale}/>
    </Fieldset>
    <Fieldset>
      <Label>{$_('modals.exportPng.options')}</Label>
      <Checkbox faded={true} labelText={$_('modals.exportPng.noFontName')} bind:checked={checked_export_without_fontname}/>
      <Checkbox faded={true} labelText={$_('modals.exportPng.transparentBg')} bind:checked={checked_export_without_bg}/>
      <Checkbox faded={true} labelText={$_('modals.exportPng.invertedColors')} bind:checked={checked_export_inverted_theme}/>
    </Fieldset>
  </span>

  <span slot="footer">
    <Button kind="accent" on:click={() => { saveImage(created_canvas) }}>Download</Button>
    &nbsp;
    <Button kind="outline" on:click={() => modals.close()}>Close</Button>
    {#if !$previews_are_zoomed}
      <div class="banner">Want to download just one of the previews?<br>Go to its details and export from there instead. <a href="/help/exporting-an-image/" target="_blank">Learn how...</a></div>
    {:else}
      <div class="banner">Are you a Cricut or Silhouette user?<br>Let us know how we can make the exports better. <a href="mailto:contact@wordmark.it">Share your thoughts</a></div>
    {/if}
  </span>
  
  <span slot="preview">
    <div class="preview-png" 
    bind:this={preview_wrapper}
    on:click={() => export_preview_zoom = !export_preview_zoom}
    on:mousemove={e => coords.set({ x: 100 * e.offsetX / e.target.offsetWidth, y: 100 * e.offsetY / e.target.offsetHeight })}
    on:mouseleave={() => { if(!export_preview_zoom) {coords.set({ x: 50, y: 50 })}}}
    class:zoom={export_preview_zoom} 
    style="background-position: {$coords.x}% {$coords.y}%">&nbsp;
    </div>
    {#if created_canvas && preview_wrapper}
      <div class="preview-scale-display">{@html export_preview_zoom ? '100%' : Math.floor(100 * preview_wrapper.offsetHeight / created_canvas.height) + '%'}</div>
    {/if}
  </span>
</Modal>

<style>
  .preview-png {
    position: relative;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    cursor: zoom-in;
  }
  .preview-png.zoom {
    background-size: initial;
    background-position: 4% 4%;
    width: calc(100% + 6rem - 0px);
    height: calc(100% + 6rem - 2px);
    margin: -3rem;
    cursor: zoom-out;
  }
  .preview-scale-display {
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translate(-50%, 0);
    padding: 0.5rem 1rem;
    font-size: 1.5rem;
    line-height: 2rem;
    color: var(--c-text-on-fill);
    background-color: var(--c-flash-bg);
    border-radius: 1.5rem;
    pointer-events: none;
    opacity: 0.5;
  }
  p {
    margin-bottom: 2rem;
  }
  p.faded {
    color: var(--c-text-faded);
  }
  .banner {
    margin: 3.5rem -3rem -3rem;
    padding: 2rem 3rem;
    background-color: var(--c-accent-fill);
    color: var(--c-text-on-fill);
  }
  .banner a {
    color: var(--c-text-faded-big);
  }
  .banner a:hover {
    color: var(--c-text-faded);
  }
</style>